'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { format } from 'date-fns';

import { EventList } from '@/components/event-row';
import { groupVenueEventRows, type VenueEventRow } from '@/services/mapVenueEventsToRows';

/** How many events to reveal per infinite-scroll page. */
const PAGE_SIZE = 10;

interface VenueEventListProps {
  /** Optional subtitle below the heading */
  description?: string;
  /** All venue events, pre-sorted chronologically and serialized on the server */
  rows: VenueEventRow[];
}

/**
 * Client wrapper around `<EventList />` for the venue page. Adds a working date
 * filter and infinite scroll: events are revealed `PAGE_SIZE` at a time as a
 * sentinel near the bottom scrolls into view, rather than rendering all at once.
 */
export const VenueEventList = ({ rows, description }: VenueEventListProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Infinite scroll stays off until the visitor clicks "View more events"; the
  // first click reveals the next page and from then on scrolling auto-loads.
  const [infiniteEnabled, setInfiniteEnabled] = useState(false);

  const filtered = useMemo(() => {
    if (!selectedDate) return rows;
    const key = format(selectedDate, 'yyyy-MM-dd');
    return rows.filter((row) => row.date === key);
  }, [rows, selectedDate]);

  const groups = useMemo(
    () => groupVenueEventRows(filtered.slice(0, visibleCount)),
    [filtered, visibleCount],
  );
  const hasMore = visibleCount < filtered.length;

  const handleDateChange = useCallback((date: Date | null) => {
    setSelectedDate(date);
    setVisibleCount(PAGE_SIZE);
    setInfiniteEnabled(false);
  }, []);

  const handleViewMore = useCallback(() => {
    setInfiniteEnabled(true);
    setVisibleCount((count) => count + PAGE_SIZE);
  }, []);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || !infiniteEnabled) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((count) => count + PAGE_SIZE);
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, infiniteEnabled]);

  return (
    <>
      <EventList
        description={description}
        groups={groups}
        filter={{
          selectedDate,
          onDateChange: handleDateChange,
          // No events at all → nothing to filter; keep enabled when a filter
          // just returned zero matches so the visitor can clear/change it.
          disabled: rows.length === 0,
        }}
        onViewMore={hasMore && !infiniteEnabled ? handleViewMore : undefined}
      />
      {hasMore && infiniteEnabled && <Box ref={sentinelRef} aria-hidden h="1px" w="100%" />}
    </>
  );
};
